import { API_URL, K8S_API_URL } from "../../helpers/constants";
import { getToken } from "../../helpers/tokenStorage";

// Re-export the shared Access helpers so Directory pages keep parity
// with the /access/* surfaces (same auth headers, same error envelope,
// same getMe shape).
export { getMe } from "../Access/api";

/**
 * Local copies of the authHeaders + jsonOrThrow helpers. Kept local so
 * this module has no circular runtime dependency on the Access folder
 * (the Access module also imports from here later once Groups + Roles
 * CRUD lands in PR-2 and moves out of the legacy screens).
 *
 * API_URL already includes the /api suffix per feedback_console_api_url.
 * Never write `${API_URL}/api/...`.
 */
const authHeaders = () => {
  const t = getToken();
  return t
    ? { Authorization: `Bearer ${t}`, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
};

async function jsonOrThrow(res) {
  if (res.ok) return res.text().then((t) => (t ? JSON.parse(t) : null));
  const body = await res.text().catch(() => "");
  const err = new Error(`HTTP ${res.status}: ${body.slice(0, 200)}`);
  err.status = res.status;
  err.body = body;
  throw err;
}

// Expose the local helpers too so per-page code can compose new calls
// without duplicating the header/error branch.
export { authHeaders, jsonOrThrow };

// ---------------- Cross-tenant listing endpoints ----------------
//
// Each returns a plain array. Coord narrows the payload to the caller's
// own account when the caller is not SYSTEM, so the same route works
// for both role bands — the ACCOUNT_ADMIN caller just sees a shorter
// list.

/**
 * Build a `?a=b&c=d` suffix from a plain object, skipping null /
 * undefined / empty-string values so callers can pass an unwrapped
 * scope payload without pruning first. Values are URI-encoded.
 */
function qs(params) {
  if (!params) return "";
  const pairs = Object.entries(params)
    .filter(([, v]) => v !== null && v !== undefined && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  return pairs.length ? `?${pairs.join("&")}` : "";
}

export function getAccounts() {
  return fetch(`${API_URL}/accounts`, { headers: authHeaders() })
    .then(jsonOrThrow)
    .then((data) => (Array.isArray(data) ? data : []));
}

/**
 * Cross-tenant users list. SYSTEM only. Optional `accountId` narrows
 * the response server-side; when omitted, coord returns every user.
 */
export function getUsers({ accountId } = {}) {
  return fetch(`${API_URL}/users${qs({ accountId })}`, {
    headers: authHeaders(),
  })
    .then(jsonOrThrow)
    .then((data) => (Array.isArray(data) ? data : []));
}

/**
 * Cross-tenant groups list. SYSTEM only (coord narrows for
 * ACCOUNT_ADMIN via bearer). Optional scope narrow:
 *   scope="SYSTEM"    → only platform-scoped groups (accountId IS NULL)
 *   scope="ACCOUNT"   → only account-scoped groups (accountId IS NOT NULL)
 *   scope=undefined   → all
 *   accountId=<uuid>  → only groups of that account (implies ACCOUNT)
 */
export function getGroups({ scope, accountId } = {}) {
  return fetch(`${API_URL}/groups${qs({ scope, accountId })}`, {
    headers: authHeaders(),
  })
    .then(jsonOrThrow)
    .then((data) => (Array.isArray(data) ? data : []));
}

/**
 * Cross-tenant roles list. SYSTEM only.
 *   accountId=<uuid>          → only custom roles owned by that account
 *   includePredefined=false   → drop predefined/platform roles
 *   includePredefined=true    → keep them (default)
 *   predefinedOnly=true       → only predefined roles (accountId IS NULL);
 *                               shorthand for "no accountId + only
 *                               platform bucket". Ignored when accountId
 *                               is also set (accountId wins).
 * When accountId is set and includePredefined is false, the response
 * is strictly the account's custom roles.
 */
export function getRoles({ accountId, includePredefined, predefinedOnly } = {}) {
  return fetch(
    `${API_URL}/roles${qs({ accountId, includePredefined, predefinedOnly })}`,
    { headers: authHeaders() }
  )
    .then(jsonOrThrow)
    .then((data) => (Array.isArray(data) ? data : []));
}

/**
 * List apps (Knative services) visible to the caller.
 *
 * Backs the APP-scope option inside the shared AssignmentPickerDialog
 * so callers can grant a role narrowly to one app instead of the whole
 * account or project.
 *
 * Coord narrows the payload to the caller's account when the caller is
 * not SYSTEM; same route works for both bands.
 */
export function getApps() {
  return fetch(`${K8S_API_URL}/kn-service/`, { headers: authHeaders() })
    .then(jsonOrThrow)
    .then((data) => (Array.isArray(data) ? data : []));
}

// Per-entity read wrappers. Detail pages call these; both use the
// singular GET so coord can 404 cleanly on stale ids without falling
// back to a full-tenant scan.
export function getGroup(gid) {
  return fetch(`${API_URL}/groups/${gid}`, { headers: authHeaders() }).then(
    jsonOrThrow
  );
}

export function getRole(rid) {
  return fetch(`${API_URL}/roles/${rid}`, { headers: authHeaders() }).then(
    jsonOrThrow
  );
}

export function getUser(uid) {
  return fetch(`${API_URL}/users/${uid}`, { headers: authHeaders() }).then(
    jsonOrThrow
  );
}

// ---------------- Account detail (drill-down) ----------------
//
// DirectoryAccountDetail hangs off `/directory/accounts/{aid}`. Coord
// serves the account envelope + its members + its groups from three
// separate routes so a SYSTEM caller and an ACCOUNT_ADMIN caller hit
// the same URLs (coord narrows the payload by caller identity).
//
// If a route 404s (older coord builds where the endpoint didn't ship
// yet), the caller can render an EmptyState rather than crashing —
// see the graceful branches inside DirectoryAccountDetail.
export function getAccount(aid) {
  return fetch(`${API_URL}/accounts/${aid}`, { headers: authHeaders() }).then(
    jsonOrThrow
  );
}

export function getAccountMembers(aid) {
  return fetch(`${API_URL}/accounts/${aid}/members`, {
    headers: authHeaders(),
  })
    .then(jsonOrThrow)
    .then((data) => (Array.isArray(data) ? data : []));
}

/**
 * Groups scoped to a single account.
 *
 * Coord's canonical route is GET /api/accounts/{aid}/groups. If a
 * deployment predates that route it 404s; callers pass
 * `{ fallbackFilter: true }` to opt into a client-side filter over
 * the cross-tenant /api/groups list instead of surfacing the 404 to
 * the user.
 */
export function getAccountGroups(aid, { fallbackFilter = false } = {}) {
  return fetch(`${API_URL}/accounts/${aid}/groups`, {
    headers: authHeaders(),
  })
    .then(jsonOrThrow)
    .then((data) => (Array.isArray(data) ? data : []))
    .catch((e) => {
      if (fallbackFilter && e && e.status === 404) {
        return fetch(`${API_URL}/groups`, { headers: authHeaders() })
          .then(jsonOrThrow)
          .then((data) =>
            Array.isArray(data)
              ? data.filter((g) => g && g.accountId === aid)
              : []
          );
      }
      throw e;
    });
}

/**
 * Invite a user into an account. Mirrors Access/api.js `inviteMember`
 * so the Directory drill-down can post the same POST without having
 * to reach into the Access module.
 * body: { email, role, canCreateProjects }
 */
export function inviteUserToAccount(aid, { email, role, canCreateProjects } = {}) {
  return fetch(`${API_URL}/accounts/${aid}/members`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({
      email,
      role: role || "MEMBER",
      canCreateProjects: !!canCreateProjects,
    }),
  }).then(jsonOrThrow);
}

/**
 * Remove a user from an account. Kept alongside `inviteUserToAccount`
 * so DirectoryAccountDetail's Users row action doesn't have to import
 * from Access/api.
 */
export function removeAccountMember(aid, userId) {
  return fetch(`${API_URL}/accounts/${aid}/members/${userId}`, {
    method: "DELETE",
    headers: authHeaders(),
  }).then(jsonOrThrow);
}

// ---------------- Account-admin invitation bootstrap ----------------
//
// SCK-621 — SYSTEM-only new-account wizard. Wraps
// POST /v1/invitations/account-admin. Coord returns the standard
// {invitation, ...} envelope; caller renders the redemption URL in a
// copy-link modal when deliveryMode is copy_only (or SES is disabled).
export function createAccountAdminInvitation({
  email,
  accountName,
  beta = false,
  trialLengthDays,
  maxProjects,
  deliveryMode = "email",
  billingInfo,
  notes,
} = {}) {
  const payload = {
    email,
    accountName,
    beta,
    deliveryMode,
  };
  if (typeof trialLengthDays === "number") payload.trialLengthDays = trialLengthDays;
  if (typeof maxProjects === "number") payload.maxProjects = maxProjects;
  if (billingInfo && Object.keys(billingInfo).length) payload.billingInfo = billingInfo;
  if (notes) payload.notes = notes;

  return fetch(`${API_URL}/v1/invitations/account-admin`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  }).then(jsonOrThrow);
}

// ---------------- Groups CRUD (PR-2, coord#333) ----------------
//
// Two scope bands mirror the coord contract:
//   - SYSTEM   → /api/groups           (cross-tenant / platform-scoped)
//   - ACCOUNT  → /api/accounts/{aid}/groups
// Callers pass `accountId` when the group is account-scoped; omit for
// SYSTEM platform-scoped groups.

/**
 * Create a group.
 *   scopeAccountId=null → POST /api/groups
 *   scopeAccountId=uuid → POST /api/accounts/{aid}/groups
 * body: { name, description? }
 */
export function createGroup({ scopeAccountId = null, name, description } = {}) {
  const body = { name };
  if (description) body.description = description;
  const url = scopeAccountId
    ? `${API_URL}/accounts/${scopeAccountId}/groups`
    : `${API_URL}/groups`;
  return fetch(url, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body),
  }).then(jsonOrThrow);
}

/**
 * Rename or edit a group.
 *   scopeAccountId=null → PUT /api/groups/{gid}
 *   scopeAccountId=uuid → PUT /api/accounts/{aid}/groups/{gid}
 * body: { name?, description? }
 */
export function updateGroup({ scopeAccountId = null, gid, name, description } = {}) {
  const body = {};
  if (typeof name === "string") body.name = name;
  if (typeof description === "string") body.description = description;
  const url = scopeAccountId
    ? `${API_URL}/accounts/${scopeAccountId}/groups/${gid}`
    : `${API_URL}/groups/${gid}`;
  return fetch(url, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(body),
  }).then(jsonOrThrow);
}

/**
 * Delete a group.
 *   scopeAccountId=null → DELETE /api/groups/{gid}
 *   scopeAccountId=uuid → DELETE /api/accounts/{aid}/groups/{gid}
 */
export function deleteGroup({ scopeAccountId = null, gid } = {}) {
  const url = scopeAccountId
    ? `${API_URL}/accounts/${scopeAccountId}/groups/${gid}`
    : `${API_URL}/groups/${gid}`;
  return fetch(url, {
    method: "DELETE",
    headers: authHeaders(),
  }).then(jsonOrThrow);
}

/**
 * Add user to a group.
 *   scopeAccountId=null → POST /api/groups/{gid}/members/{uid}
 *   scopeAccountId=uuid → POST /api/accounts/{aid}/groups/{gid}/members/{uid}
 * Coord returns 204 or the updated group summary; we tolerate either.
 */
export function addGroupMember({ scopeAccountId = null, gid, userId } = {}) {
  const url = scopeAccountId
    ? `${API_URL}/accounts/${scopeAccountId}/groups/${gid}/members/${userId}`
    : `${API_URL}/groups/${gid}/members/${userId}`;
  return fetch(url, {
    method: "POST",
    headers: authHeaders(),
  }).then(jsonOrThrow);
}

/**
 * Remove user from a group.
 *   scopeAccountId=null → DELETE /api/groups/{gid}/members/{uid}
 *   scopeAccountId=uuid → DELETE /api/accounts/{aid}/groups/{gid}/members/{uid}
 */
export function removeGroupMember({ scopeAccountId = null, gid, userId } = {}) {
  const url = scopeAccountId
    ? `${API_URL}/accounts/${scopeAccountId}/groups/${gid}/members/${userId}`
    : `${API_URL}/groups/${gid}/members/${userId}`;
  return fetch(url, {
    method: "DELETE",
    headers: authHeaders(),
  }).then(jsonOrThrow);
}

// ---------------- Role assignments (PR-2, coord#333) ----------------
//
// Two POST/DELETE surfaces:
//   - SYSTEM: /api/groups/{gid}/role-assignments
//     body: { roleId } — assigns a PLATFORM (or SYSTEM) role to a group.
//   - ACCOUNT: /api/accounts/{aid}/role-assignments
//     body: { subjectType: "USER" | "GROUP", subjectId, roleId }
//     Polymorphic subject: same route grants a role to either a user
//     or a group inside the account.
//
// Callers pick the route by which target owns the assignment. Group
// detail → group route. Account detail (or user detail) → account
// route.

/**
 * Assign a role. Two modes:
 *   { groupId, roleId }              → POST /api/groups/{gid}/role-assignments
 *   { accountId, subjectType, subjectId, roleId }
 *                                    → POST /api/accounts/{aid}/role-assignments
 *
 * Optional narrowing (both modes):
 *   { resourceType: "PROJECT" | "APP", resourceId }
 * When present, the grant is scoped to a single project or app inside
 * the enclosing account; when absent, the grant covers the whole
 * account (or platform, for the group→system path).
 */
export function createRoleAssignment(args) {
  if (args && args.groupId) {
    const body = { roleId: args.roleId };
    if (args.resourceType) body.resourceType = args.resourceType;
    if (args.resourceId) body.resourceId = args.resourceId;
    return fetch(`${API_URL}/groups/${args.groupId}/role-assignments`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(body),
    }).then(jsonOrThrow);
  }
  if (args && args.accountId) {
    const body = {
      subjectType: args.subjectType,
      subjectId: args.subjectId,
      roleId: args.roleId,
    };
    if (args.resourceType) body.resourceType = args.resourceType;
    if (args.resourceId) body.resourceId = args.resourceId;
    return fetch(`${API_URL}/accounts/${args.accountId}/role-assignments`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(body),
    }).then(jsonOrThrow);
  }
  return Promise.reject(
    Object.assign(new Error("createRoleAssignment: missing groupId or accountId"), {
      status: 400,
    })
  );
}

/**
 * Revoke a role assignment. Same routing rules as createRoleAssignment.
 *   { groupId, roleId }              → DELETE /api/groups/{gid}/role-assignments (body)
 *   { accountId, subjectType, subjectId, roleId }
 *                                    → DELETE /api/accounts/{aid}/role-assignments (body)
 */
export function revokeRoleAssignment(args) {
  if (args && args.groupId) {
    return fetch(`${API_URL}/groups/${args.groupId}/role-assignments`, {
      method: "DELETE",
      headers: authHeaders(),
      body: JSON.stringify({ roleId: args.roleId }),
    }).then(jsonOrThrow);
  }
  if (args && args.accountId) {
    return fetch(`${API_URL}/accounts/${args.accountId}/role-assignments`, {
      method: "DELETE",
      headers: authHeaders(),
      body: JSON.stringify({
        subjectType: args.subjectType,
        subjectId: args.subjectId,
        roleId: args.roleId,
      }),
    }).then(jsonOrThrow);
  }
  return Promise.reject(
    Object.assign(new Error("revokeRoleAssignment: missing groupId or accountId"), {
      status: 400,
    })
  );
}

// ---------------- Roles CRUD (PR-2, coord#333) ----------------
//
// SYSTEM predefined roles: POST/PUT/DELETE /api/roles
// ACCOUNT_ADMIN custom roles: same route, coord infers `accountId`
// from the caller when `predefined:false` is sent.

/**
 * Create a role.
 * body: { name, description?, predefined?: bool, accountId? }
 * Coord clamps: non-SYSTEM callers cannot set predefined=true.
 */
export function createRole({ name, description, predefined = false, accountId } = {}) {
  const body = { name, predefined };
  if (description) body.description = description;
  if (accountId) body.accountId = accountId;
  return fetch(`${API_URL}/roles`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body),
  }).then(jsonOrThrow);
}

/**
 * Update a role.
 * body: { name?, description? }
 * Coord refuses to mutate predefined roles' name; UI hides the edit CTA
 * on the row anyway, so this only fires for custom roles in practice.
 */
export function updateRole({ rid, name, description } = {}) {
  const body = {};
  if (typeof name === "string") body.name = name;
  if (typeof description === "string") body.description = description;
  return fetch(`${API_URL}/roles/${rid}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(body),
  }).then(jsonOrThrow);
}

export function deleteRole({ rid } = {}) {
  return fetch(`${API_URL}/roles/${rid}`, {
    method: "DELETE",
    headers: authHeaders(),
  }).then(jsonOrThrow);
}

/**
 * Add an entitlement to a role (SYSTEM only).
 * body: { entitlement } — coord's enum value ("apps.write", etc.)
 */
export function addEntitlement({ rid, entitlement } = {}) {
  return fetch(`${API_URL}/roles/${rid}/entitlements`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ entitlement }),
  }).then(jsonOrThrow);
}

/**
 * Remove an entitlement from a role (SYSTEM only).
 */
export function removeEntitlement({ rid, entitlement } = {}) {
  return fetch(`${API_URL}/roles/${rid}/entitlements`, {
    method: "DELETE",
    headers: authHeaders(),
    body: JSON.stringify({ entitlement }),
  }).then(jsonOrThrow);
}
